/**
 * Minimal markdown parser for note text, photo captions and status reasons
 * (JC-114).
 *
 * Notes support a deliberately tiny subset: bold, italic, bullet lists,
 * task lists (`- [ ]` / `- [x]`), line breaks, headers (all rendered as a
 * bold line), and links. Nothing else — no images, no tables, no raw HTML.
 * The parser produces a small AST; rendering to React happens in
 * components/NoteMarkdown.tsx, so no HTML string is ever injected.
 */

export type InlineNode =
	| { kind: "text"; text: string }
	| { kind: "bold"; children: InlineNode[] }
	| { kind: "italic"; children: InlineNode[] }
	| { kind: "link"; label: string; href: string };

export type TaskItem = {
	checked: boolean;
	children: InlineNode[];
	/**
	 * Position of this task among ALL task items in the document, in source
	 * order. This is the handle `toggleTaskInMarkdown` takes, so a rendered
	 * checkbox can name the exact source line it toggles.
	 */
	taskIndex: number;
};

export type BlockNode =
	/** Consecutive non-blank lines; single newlines inside stay visible breaks. */
	| { kind: "paragraph"; lines: InlineNode[][] }
	/** Any `#`-`######` heading — all render the same (a bold line). The level
	 * is kept only so an edit round-trips the author's original marker. */
	| { kind: "heading"; children: InlineNode[]; level: number }
	| { kind: "bullets"; items: InlineNode[][] }
	| { kind: "tasks"; items: TaskItem[] };

/** Links only render as anchors for schemes that cannot execute script. */
export const isSafeLinkHref = (href: string) =>
	/^(https?:|mailto:|tel:)/i.test(href.trim());

const INLINE_PATTERN =
	/\[([^\]\n]+)\]\(([^()\s]+)\)|(\*\*|__)(.+?)\3|\*([^*\n]+)\*|_([^_\n]+)_/g;

export function parseInline(text: string): InlineNode[] {
	const nodes: InlineNode[] = [];
	let cursor = 0;
	const pattern = new RegExp(INLINE_PATTERN);

	for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
		if (match.index > cursor) {
			nodes.push({ kind: "text", text: text.slice(cursor, match.index) });
		}
		const [full, linkLabel, linkHref, , boldBody, starBody, underscoreBody] =
			match;
		if (linkLabel !== undefined && linkHref !== undefined) {
			if (isSafeLinkHref(linkHref)) {
				nodes.push({ kind: "link", label: linkLabel, href: linkHref });
			} else {
				nodes.push({ kind: "text", text: linkLabel });
			}
		} else if (boldBody !== undefined) {
			nodes.push({ kind: "bold", children: parseInline(boldBody) });
		} else {
			const body = starBody ?? underscoreBody ?? "";
			nodes.push({ kind: "italic", children: parseInline(body) });
		}
		cursor = match.index + full.length;
	}

	if (cursor < text.length) {
		nodes.push({ kind: "text", text: text.slice(cursor) });
	}
	return nodes;
}

const HEADING_PATTERN = /^(#{1,6})\s+(.*)$/;
const BULLET_PATTERN = /^\s*[-*+]\s+(.*)$/;
/** Must be tried before BULLET_PATTERN — a task line is also a bullet line. */
const TASK_PATTERN = /^\s*[-*+]\s+\[( |x|X)\]\s+(.*)$/;

export function parseNoteMarkdown(text: string): BlockNode[] {
	const blocks: BlockNode[] = [];
	let paragraph: InlineNode[][] = [];
	let bullets: InlineNode[][] = [];
	let tasks: TaskItem[] = [];
	let taskCount = 0;

	const flushParagraph = () => {
		if (paragraph.length) {
			blocks.push({ kind: "paragraph", lines: paragraph });
			paragraph = [];
		}
	};
	const flushBullets = () => {
		if (bullets.length) {
			blocks.push({ kind: "bullets", items: bullets });
			bullets = [];
		}
	};
	const flushTasks = () => {
		if (tasks.length) {
			blocks.push({ kind: "tasks", items: tasks });
			tasks = [];
		}
	};

	for (const line of text.split(/\r?\n/)) {
		if (!line.trim()) {
			flushParagraph();
			flushBullets();
			flushTasks();
			continue;
		}
		const heading = HEADING_PATTERN.exec(line);
		if (heading) {
			flushParagraph();
			flushBullets();
			flushTasks();
			blocks.push({
				kind: "heading",
				children: parseInline(heading[2]),
				level: heading[1].length,
			});
			continue;
		}
		const task = TASK_PATTERN.exec(line);
		if (task) {
			flushParagraph();
			flushBullets();
			tasks.push({
				checked: task[1] !== " ",
				children: parseInline(task[2]),
				taskIndex: taskCount++,
			});
			continue;
		}
		const bullet = BULLET_PATTERN.exec(line);
		if (bullet) {
			flushParagraph();
			flushTasks();
			bullets.push(parseInline(bullet[1]));
			continue;
		}
		flushBullets();
		flushTasks();
		paragraph.push(parseInline(line));
	}

	flushParagraph();
	flushBullets();
	flushTasks();
	return blocks;
}

const TASK_MARKER_PATTERN = /^(\s*[-*+]\s+\[)( |x|X)(\]\s+)/;

/**
 * Flip the checked state of the taskIndex-th task item (source order, as
 * numbered by `parseNoteMarkdown`) and return the new markdown. Everything
 * else in the text — whitespace, marker style, line endings — is untouched,
 * so a checkbox toggle diffs as exactly one character in the revision trail.
 * Returns null when the text has no such task (the note changed underneath).
 */
export function toggleTaskInMarkdown(
	text: string,
	taskIndex: number,
): string | null {
	// Split retaining the delimiters so reassembly preserves \r\n exactly.
	const parts = text.split(/(\r?\n)/);
	let seen = 0;
	for (let i = 0; i < parts.length; i += 2) {
		const marker = TASK_MARKER_PATTERN.exec(parts[i]);
		if (!marker) continue;
		if (seen === taskIndex) {
			const flipped = marker[2] === " " ? "x" : " ";
			parts[i] =
				marker[1] + flipped + parts[i].slice(marker[1].length + 1);
			return parts.join("");
		}
		seen += 1;
	}
	return null;
}
